import type React from "react"
import { Children, cloneElement, useRef } from "react"

import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

type DraggableProps = {
  id: string
  children: React.ReactNode
  placeholder?: boolean
  overlay?: React.ReactNode
  disabled?: boolean
  isAlsoDragging?: boolean
  placeholderOpacity?: number
  style?: React.CSSProperties
  attachToChild?: boolean
}

export const Draggable = ({
  id,
  children,
  overlay,
  isAlsoDragging,
  placeholder,
  disabled,
  placeholderOpacity = 0.2,
  style,
  attachToChild,
}: DraggableProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, active } = useSortable({
    id,
    disabled: placeholder || disabled,
    data: {
      overlay: overlay || children,
    },
  })

  // A draggable can be manually marked to be visually dragged by the outside
  const isDragging = isAlsoDragging || (!!active?.id && active?.id === id)

  const transformStyle = {
    transform: placeholder ? null : CSS.Transform.toString(transform),
    transition: placeholder ? null : transition,
    opacity: isDragging ? placeholderOpacity : 1, // Hide the original element when dragging, if overlay is visible
    cursor: placeholder || disabled ? "default" : "grab",
    pointerEvents: isDragging ? "none" : "auto",
    ...(style || {}),
  }

  if (attachToChild) {
    const child = Children.only(children) as React.ReactElement
    return cloneElement(child, {
      ref: (ref) => {
        setNodeRef(ref)
      },
      style: { ...child.props.style, ...transformStyle },
      ...attributes,
      ...listeners,
    })
  }

  return (
    <div
      ref={(ref) => {
        setNodeRef(ref)
      }}
      style={transformStyle}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  )
}
